import { PublicLayout } from "@/components/layout/PublicLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Phone, MapPin, Clock, MessageCircle, Users } from "lucide-react";
import { useState } from "react";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log("Form submitted:", formData);
  };

  return (
    <PublicLayout
      showHero={true}
      heroTitle="Kontakt & Support"
      heroSubtitle="Haben Sie Fragen zu unserer KI-Hörbuch-Plattform? Unser Expertenteam steht Ihnen gerne zur Verfügung."
      heroVariant="compact"
    >
      {/* Contact Information */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="minimal-card">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold mb-6 text-gray-900">Nachricht senden</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Ihr vollständiger Name"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">E-Mail</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="ihre@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Betreff</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      placeholder="Worum geht es?"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Nachricht</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                      rows={5}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gray-900 text-white hover:bg-gray-800"
                  >
                    Nachricht senden
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className="space-y-8">
              <Card className="minimal-card">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mr-4">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">E-Mail Support</h3>
                      <p className="text-gray-600">info@salesangels.org</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-500">Antwortzeit: Innerhalb von 24 Stunden</p>
                </CardContent>
              </Card>

              <Card className="minimal-card">
                <CardContent className="p-8">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center mr-4">
                      <MapPin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Unser Büro</h3>
                      <p className="text-gray-600">
                        Schulhausgässli 4<br />
                        3098 Köniz, Schweiz
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Hier finden Sie Antworten auf die meistgestellten Fragen zu unserer Plattform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="minimal-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Wie lange dauert die Hörbuch-Erstellung?
                </h3>
                <p className="text-gray-600">
                  Mit unserer KI-Technologie können Sie ein komplettes Hörbuch in durchschnittlich 7
                  Minuten erstellen, von der Ideeneingabe bis zur fertigen Audiodatei.
                </p>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Welche Plattformen werden unterstützt?
                </h3>
                <p className="text-gray-600">
                  Wir unterstützen alle wichtigen Hörbuch-Plattformen wie Audible, Spotify, Apple
                  Podcasts, Google Podcasts und viele weitere.
                </p>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Wie funktioniert die Qualitätskontrolle?
                </h3>
                <p className="text-gray-600">
                  Unsere KI verwendet fortschrittliche Algorithmen zur Qualitätssicherung und prüft
                  sowohl Inhalt als auch Audioqualität automatisch.
                </p>
              </CardContent>
            </Card>

            <Card className="minimal-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Kann ich die Stimmen anpassen?
                </h3>
                <p className="text-gray-600">
                  Ja, Sie haben Zugang zu über 200 verschiedenen KI-Stimmen in mehreren Sprachen und
                  können Geschwindigkeit und Tonfall anpassen.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Noch Fragen?</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Buchen Sie eine kostenlose Live-Demonstration und lassen Sie sich alle Funktionen
            persönlich zeigen.
          </p>

          <Button
            size="lg"
            className="bg-gray-900 text-white hover:bg-gray-800 px-8 py-4 text-lg font-semibold"
          >
            Live Demo buchen
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Contact;
